import { ReactNode, useEffect, useState } from "react";
import cx from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBoxesPacking, faEnvelope, faLocationDot, faPhone, faTruck, faUser } from "@fortawesome/free-solid-svg-icons";

import styles from "./style.module.scss";
import { SuscriptionDataType } from "@/api/SuscriptionsApi";
import { InvoiceDataType } from "@/api/InvoiceApi";
import { generateS3AssetUrl } from "@utils/FormFileServer";
import { stripHtmlTags } from "@utils/Utils";
import PrintPage from "../PrintPage";

/** Boletas que caben en una pagina carta. Debe coincidir con `$guides-per-page` del SCSS. */
export const SHIPPING_GUIDES_PER_PAGE = 4;

type ShippingGuideProps = {
  suscription?: SuscriptionDataType,
  cliente: string,
  direccion: string,
  telefono: string,
  transporte: string,
  bultos: number,
}

/**
 * Una boleta de guia de envio: la etiqueta que va pegada en la caja. Es el equivalente al
 * Excel que el cliente llenaba a mano, con los datos del negocio arriba y los del destino
 * abajo.
 *
 * Se separa del resto de la impresion a proposito: la reimpresion desde la lista de facturas
 * y la impresion posterior a facturar montan exactamente este mismo componente.
 */
const ShippingGuide = (props: ShippingGuideProps) => {
  const { suscription, cliente, direccion, telefono, transporte, bultos } = props;
  const [logo, setLogo] = useState<URL>();

  useEffect(() => {
    loadLogo();
  }, [suscription]);

  const loadLogo = async () => {
    if (suscription?.logo) {
      const asset = await generateS3AssetUrl(suscription.logo);
      setLogo(asset.url);
    }
  }

  return (
    <div className={styles.guide} data-testid="shipping-guide">
      <div className={styles.header}>
        <div className={styles.logo}>
          {logo && (<img src={`${logo}`} />)}
        </div>
        <div className={styles.business}>
          <div className={styles.businessName}>{suscription?.nombre}</div>
          <div>Cédula Jurídica N° {suscription?.identificacion}</div>
          {suscription?.nombre_comercial && (<div>{suscription.nombre_comercial}</div>)}
          {suscription?.email && (
            <div>
              <FontAwesomeIcon className={cx(styles.icon, styles.businessIcon)} icon={faEnvelope} /> {suscription.email}
            </div>
          )}
          <div>
            <FontAwesomeIcon className={cx(styles.icon, styles.businessIcon)} icon={faPhone} /> {suscription?.telefono1}
            {suscription?.telefono2 ? ` //  ${suscription.telefono2}` : ""}
          </div>
        </div>
      </div>
      <div className={styles.rows}>
        <div className={styles.row}>
          <label><FontAwesomeIcon className={styles.icon} icon={faUser} /> CLIENTE:</label>
          <span data-testid="shipping-guide-cliente">{cliente}</span>
        </div>
        <div className={styles.row}>
          <label><FontAwesomeIcon className={styles.icon} icon={faLocationDot} /> DIRECCION:</label>
          <span data-testid="shipping-guide-direccion">{stripHtmlTags(`${direccion || ""}`)}</span>
        </div>
        <div className={styles.row}>
          <label><FontAwesomeIcon className={styles.icon} icon={faPhone} /> TELEFONO:</label>
          <span data-testid="shipping-guide-telefono">{telefono}</span>
        </div>
        <div className={styles.splitRow}>
          <div className={styles.row}>
            <label><FontAwesomeIcon className={styles.icon} icon={faTruck} /> TRANSPORTE:</label>
            <span data-testid="shipping-guide-transporte">{transporte}</span>
          </div>
          <div className={styles.bultos}>
            <label><FontAwesomeIcon className={styles.icon} icon={faBoxesPacking} /> BULTOS:</label>
            <span data-testid="shipping-guide-bultos">{bultos}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

type ShippingGuidePagesProps = {
  invoice: InvoiceDataType,
  suscription?: SuscriptionDataType,
  /** Solo la ruta independiente de reimpresion dispara la impresion desde aqui. */
  autoPrint?: boolean,
}

/**
 * Las paginas de guia de una factura ya persistida. Se imprime una boleta por bulto (el
 * transportista se queda con una por caja) y caben cuatro por pagina.
 *
 * Todo sale de la factura, no del cliente: `nombre_factura`, `direccion` y los telefonos son
 * la foto del cliente al momento de facturar, y `shipping_carrier` quedo congelado ahi mismo.
 */
export const ShippingGuidePages = (props: ShippingGuidePagesProps) => {
  const { invoice, suscription, autoPrint = false } = props;
  const bultos = Number(invoice?.bultos || 0);

  if (bultos <= 0) return null;

  const telefono = [invoice.tel1, invoice.tel2].filter((tel) => !!`${tel || ""}`.trim()).join(" / ");
  const pages: ReactNode[] = [];

  for (let start = 0; start < bultos; start += SHIPPING_GUIDES_PER_PAGE) {
    const guidesInPage = Math.min(SHIPPING_GUIDES_PER_PAGE, bultos - start);
    pages.push(
      <PrintPage key={`shipping-guide-page-${start}`} className={styles.guidePage} autoPrint={autoPrint}>
        {Array.from({ length: guidesInPage }).map((_unused, index) => (
          <ShippingGuide
            key={`shipping-guide-${start + index}`}
            suscription={suscription}
            cliente={`${invoice.nombre_factura || ""}`}
            direccion={`${invoice.direccion || ""}`}
            telefono={telefono}
            transporte={`${invoice.shipping_carrier || ""}`}
            bultos={bultos}
          />
        ))}
      </PrintPage>
    );
  }

  return (<>{pages}</>);
}

export default ShippingGuide;
